/**
 * Error Message Component
 * Displays error messages with optional dismiss
 */

import { AlertCircle, X } from 'lucide-react';
import { cn } from '@/utils/classNames';

export type ErrorSeverity = 'error' | 'warning' | 'info';

export interface ErrorMessageProps {
  message: string;
  severity?: ErrorSeverity;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

const severityStyles: Record<ErrorSeverity, string> = {
  error: cn(
    'bg-error-light border-error text-error-dark'
  ),
  warning: cn(
    'bg-warning-light border-warning text-warning-dark'
  ),
  info: cn(
    'bg-info-light border-info text-info-dark'
  ),
};

export function ErrorMessage({
  message,
  severity = 'error',
  dismissible = false,
  onDismiss,
  className,
}: ErrorMessageProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-lg',
        'border-l-4 p-4',
        severityStyles[severity],
        className
      )}
      role="alert"
    >
      <AlertCircle
        className="h-5 w-5 flex-shrink-0 mt-0.5"
        aria-hidden="true"
      />
      <p className="flex-1 text-sm font-medium">{message}</p>
      {dismissible && onDismiss && (
        <button
          onClick={onDismiss}
          className={cn(
            'flex-shrink-0 rounded-md p-1',
            'hover:bg-black/10 transition-colors',
            'focus-visible:outline-none focus-visible:ring-2',
            'focus-visible:ring-offset-1'
          )}
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
